import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import { getRequestVerticalOverride } from "@services/verticals";
import { canPreviewOtherVerticals } from "@utils/userUtils";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";

/**
 * Build the headers used by every facility-compliance fetch call. Mirrors
 * what `authenticatedFetch` does:
 * Content-Type + Authorization Bearer + session vertical override
 * (`X-Preview-Vertical` for exec preview or aviation landing lock).
 */
function _buildAuthAndPreviewHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getValidAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const override = getRequestVerticalOverride();
  if (override) {
    headers["X-Preview-Vertical"] = override;
  } else if (
    sessionStorage.getItem("unicomply.previewVertical") &&
    !canPreviewOtherVerticals()
  ) {
    sessionStorage.removeItem("unicomply.previewVertical");
  }
  return headers;
}

/**
 * Apply the active session vertical override to a facility-compliance
 * payload by overriding `compliance_type` and `vertical` (when present in the
 * payload). The server treats the X-Preview-Vertical header as authoritative
 * for allowed overrides, but stamping the body too keeps older clients aligned.
 *
 * The function is a no-op when no override slug is set or the payload is null.
 */
function _applyPreviewVerticalToPayload<T extends Record<string, unknown>>(
  payload: T | null | undefined,
): T | null | undefined {
  if (!payload) return payload;
  const override = getRequestVerticalOverride();
  if (!override) {
    if (
      sessionStorage.getItem("unicomply.previewVertical") &&
      !canPreviewOtherVerticals()
    ) {
      sessionStorage.removeItem("unicomply.previewVertical");
    }
    return payload;
  }
  const out: Record<string, unknown> = { ...payload };
  out.compliance_type = override;
  out.vertical = override;
  return out as T;
}

export interface Citation {
  title: string;
  url: string;
  publisher: string;
  publicationDate: string | null;
  accessedDate: string;
}

export interface Condition {
  text: string;
  citation: Citation;
}

export interface Amount {
  amount: number;
  currency: string;
  isEstimate: boolean;
  notes?: string;
}

export interface Timeline {
  applicationDate: string;
  reviewDate: string;
  issuanceDate: string;
  expirationDate: string;
}

export interface Finding {
  text: string;
  citation: Citation;
}

export interface CertificateOfNeedSummary {
  projectDescription: string;
  conditionsOfApproval: Condition[];
  approvedCapitalCost: Amount;
  timeline: Timeline;
  findings: {
    need: Finding;
    financialFeasibility: Finding;
    structureProcessOfCare: Finding;
    costContainment: Finding;
  };
  financialFeasibilityAnalysis: {
    capitalCost: Amount;
    payerMix: {
      Medicare: number;
      Medicaid: number;
      Commercial: number;
      SelfPay: number;
    };
    charityCarePercent: number;
    notes: Finding;
  };
  criteriaSatisfied: string[];
  costContainmentDiscussion: Finding;
  publicComments: Array<{
    quote: string;
    submitter: string;
    citation: Citation;
  }>;
  screeningResponses: Finding;
  statusCheck: {
    currentLicenseUnits: number;
    cnCompletionVerified: any;
    followUps: Condition[];
  };
  bedNeedMethodology: {
    methodSummary: Finding;
    occupancyTargets: number[];
    projections: Array<{
      year: number;
      projectedNeed: number;
      utilizationRate: number;
    }>;
  };
  financialProjections: Array<{
    label: string;
    amount: Amount;
    notes: string | null;
  }>;
}

export interface LicenseRequirement {
  name: string;
  category: string;
  fee: Amount;
  feeBasis: string;
  formula: any;
  workedExample: any;
  renewalCycle: string;
  riskIfNotMaintained: string;
  citation: Citation;
}

export interface LicensingRequirements {
  country: LicenseRequirement[];
  state: LicenseRequirement[];
  county: LicenseRequirement[];
  city: LicenseRequirement[];
}

export interface AccreditationRequirements {
  conditionsOfParticipation: string[];
  accreditingOrganizations: Array<{
    name: string;
    deemedStatusRequirements: string;
    citation: Citation;
  }>;
  programSpecific: Array<{
    program: string;
    requirements: string;
    citation: Citation;
  }>;
}

export interface ComplianceItem {
  text: string;
  citation: Citation;
}

export interface Deadline {
  name: string;
  rule: string;
  nextDueDate: string;
  frequency: string;
  citation: Citation;
}

export interface ComplianceComparison {
  completed: ComplianceItem[];
  pending: ComplianceItem[];
  updates: ComplianceItem[];
  deadlines: Deadline[];
}

export interface AuditChecklistItem {
  name: string;
  description: string;
  required: boolean;
  status: string;
  files: any[];
  citation: Citation;
}

export interface PenaltyItem {
  jurisdiction: string;
  name: string;
  regulationSource: string;
  ruleOrQuote: string;
  formula: string;
  workedExample: {
    inputs: { [key: string]: any };
    calculationNotes: string;
    result: Amount;
  };
  riskOrNonCompliance: string;
  citation: Citation;
}

export interface ComplianceCalendarItem {
  deadline: string;
  item: string;
  requirement: string;
  penaltyIfMissed: string;
  estimatedCostExposure: string;
  citation: Citation;
  required?: boolean;
  /** When set to "ongoing", the row is a standing duty — not a specific filing deadline. */
  cadence?: string;
  /** "none" means no calendar due date; use with cadence for event-driven obligations. */
  deadlineType?: string;
}

export interface PenaltiesAndTotalExposure {
  items: PenaltyItem[];
  rollup: {
    recurringAnnualCosts: Array<{
      label: string;
      amount: Amount;
    }>;
    contingentRisks: ComplianceItem[];
    assumptions: {
      licensedUnits: number;
      fteCount: any;
      priorYearRevenue: Amount;
    };
  };
  complianceCalendar: ComplianceCalendarItem[];
}

export interface EntityDetails {
  organization: string;
  facility_name: string;
  facility_type: string;
  country: string;
  state: string;
  county: string;
  city: string;
  siteUrl: string;
  documents: string[];
  asOfDate: string;
}

/** Facility context added by backend (e.g. revalidation dates from DB) for display in UI */
export interface FacilityContext {
  last_medicare_revalidation_date?: string | null;
  last_medicaid_revalidation_date?: string | null;
  /** Medicare certification date (CMS); shown when revalidation date is not on record */
  medicare_certification_date?: string | null;
}

/**
 * When a pending item says "last revalidation date not on record", replace with actual
 * revalidation date(s) from facilityContext so the UI shows the date when we have it.
 * Only uses last_medicare_revalidation_date / last_medicaid_revalidation_date (no fallback).
 */
export function getPendingItemDisplayText(
  item: { text?: string } | string,
  facilityContext?: FacilityContext | null
): string {
  const t = typeof item === "string" ? item : (item?.text ?? "");
  if (!t || !facilityContext) return t;
  if (!/not on record/i.test(t)) return t;
  const parts: string[] = [];
  if (facilityContext.last_medicare_revalidation_date) {
    parts.push(`Last Medicare revalidation: ${facilityContext.last_medicare_revalidation_date}`);
  }
  if (facilityContext.last_medicaid_revalidation_date) {
    parts.push(`Last Medicaid revalidation: ${facilityContext.last_medicaid_revalidation_date}`);
  }
  if (parts.length === 0) return t;
  return t.replace(/last revalidation date not on record\.?/gi, parts.join("; "));
}

/** Accreditation program section from rules engine (e.g. NIAHO DNV from aggregator). */
export interface AccreditationProgramSection {
  id?: string;
  title?: string;
  content?: string;
  citation?: { title?: string; url?: string };
  frequency?: string | null;
  scheduleDescription?: string | null;
}

/** Calendar item for accreditation schedule (frequency/scheduleDescription). */
export interface AccreditationCalendarItem {
  item?: string;
  frequency?: string | null;
  scheduleDescription?: string | null;
  citation?: { title?: string; url?: string };
}

/** A single CFR regulation section returned by the aggregator for a facility type. */
export interface CfrRegulationSection {
  section_identifier: string;
  section_name: string;
  ecfr_identifier?: string;
}

/** A CFR regulation part grouping sections from the aggregator. */
export interface CfrRegulationPart {
  part_number: string;
  part_name: string;
  cfr_title: number;
  group_short_name: string;
  group_name: string;
  sections: CfrRegulationSection[];
}

/** Aggregator-sourced CFR regulation sections linked to this facility type. */
export interface CfrRegulationSections {
  parts: CfrRegulationPart[];
  total_sections: number;
}

/** Single phrase->URL mapping for in-text reference links (e.g. "42 CFR Part 482" -> eCFR URL). Sourced from DB/API so the UI does not hardcode phrases or URLs. */
export interface AccreditationReferenceLink {
  phrase: string;
  url: string;
}

/** Program-level links (generic: any vertical, any accreditation). */
export interface AccreditationProgramLinks {
  regulationUrl?: string;
  standardsUrl?: string;
  portalUrl?: string;
  /** Phrase-to-URL mappings for reference/citation links (e.g. "42 CFR Part 482", "FGI Guidelines"). When provided, UI uses these instead of hardcoded links; when empty or absent, no in-text phrase links are rendered. */
  referenceLinks?: AccreditationReferenceLink[];
}

/** Single accreditation program (e.g. niahoDnv) under sections.otherRequirements. */
export interface AccreditationProgramData {
  program?: string;
  programSlug?: string;
  /** Optional primary standard code (e.g. TJC PC.03.01.01) when the program represents a single standard. When set, UI can show it in the accordion title. */
  standardCode?: string;
  /** Optional subject/discipline (e.g. Anesthesiology, Anesthesia Care). When set, UI can show it in the accordion title. */
  subject?: string;
  countryCode?: string;
  vertical?: string;
  revision?: string;
  effectiveDate?: string;
  sections?: AccreditationProgramSection[];
  calendarItems?: AccreditationCalendarItem[];
  programLinks?: AccreditationProgramLinks;
}

/** Response shape from GET /api/v1/accreditations (same as sections.otherRequirements). */
export interface AccreditationsResponse {
  niahoDnv?: AccreditationProgramData;
  [key: string]: AccreditationProgramData | undefined;
}

/** A single accreditation program entry within a body's programs list. */
export interface BodyProgramEntry {
  programSlug?: string;
  program?: string;
  sections?: Array<{
    id?: string;
    title?: string;
    content?: string;
    citation?: { title?: string; url?: string } | string;
    frequency?: string;
    scheduleDescription?: string;
  }>;
  [key: string]: unknown;
}

/** Per-body data returned by GET /api/v1/accreditations/?body_slugs=... */
export interface BodyProgramData {
  bodySlug?: string;
  bodyAcronym?: string;
  fullName?: string;
  websiteUrl?: string;
  cmsDeemed?: boolean;
  overview?: string;
  programs?: BodyProgramEntry[];
}

/** Response shape from multi-body mode: keyed by body_slug. */
export interface MultiBodyAccreditationsResponse {
  [bodySlug: string]: BodyProgramData;
}

/** A single accrediting body entry returned by GET /api/v1/accreditations/bodies. */
export interface AccreditingBodyEntry {
  bodySlug: string;
  bodyAcronym: string;
  fullName: string;
  websiteUrl?: string;
  cmsDeemed?: boolean;
  foundedYear?: number;
  hqLocation?: string;
  categories?: string[];
  overview?: string;
  countryCode?: string;
  vertical?: string;
  applicableProgramSlugs?: string[];
}

/** Response from GET /api/v1/accreditations/bodies. */
export interface AccreditingBodiesResponse {
  bodies: AccreditingBodyEntry[];
}

/** Primary standard code for display (e.g. "PC.03.01.01"). Uses standardCode from API when present. Only derives from first calendar item or section when the program has a single section/item (single-standard program); avoids showing one section's code (e.g. "QM") for multi-section programs like NIAHO DNV. */
export function getAccreditationProgramStandardCode(program: AccreditationProgramData | null | undefined): string | undefined {
  if (!program) return undefined;
  if (program.standardCode?.trim()) return program.standardCode.trim();
  const calendarCount = program.calendarItems?.length ?? 0;
  const sectionCount = program.sections?.length ?? 0;
  const isSingleStandard = calendarCount <= 1 && sectionCount <= 1;
  if (!isSingleStandard) return undefined;
  const firstItem = program.calendarItems?.[0]?.item;
  if (firstItem?.trim()) {
    const code = firstItem.split(/\s*[—–-]\s*/)[0]?.trim();
    if (code) return code;
  }
  const firstTitle = program.sections?.[0]?.title;
  if (firstTitle?.trim()) {
    const code = firstTitle.split(/\s*[—–-]\s*/)[0]?.trim();
    if (code) return code;
  }
  return undefined;
}

/** Derive subject/topic for display (e.g. "Anesthesia Care", "Anesthesiology"). Uses subject from API when present, otherwise from first calendar item (part after " - ") or section title (part after " — "). */
export function getAccreditationProgramSubject(program: AccreditationProgramData | null | undefined): string | undefined {
  if (!program) return undefined;
  if (program.subject?.trim()) return program.subject.trim();
  const firstItem = program.calendarItems?.[0]?.item;
  if (firstItem?.trim()) {
    const parts = firstItem.split(/\s*[—–-]\s*/);
    const subject = parts[parts.length - 1]?.trim();
    if (subject && parts.length > 1) return subject;
  }
  const firstSectionTitle = program.sections?.[0]?.title;
  if (firstSectionTitle?.trim()) {
    const parts = firstSectionTitle.split(/\s*[—–-]\s*/);
    const subject = parts[parts.length - 1]?.trim();
    if (subject && parts.length > 1) return subject;
  }
  return undefined;
}

export interface FacilityComplianceData {
  entityDetails?: EntityDetails;
  generatedAt?: string;
  /** Revalidation dates from facility record; use to show "Last revalidation: YYYY-MM-DD" instead of "not on record" */
  facilityContext?: FacilityContext;
  /**
   * Global administrators only: exact strings sent to the LLM per section
   * (`licensing`, `accreditation`, `con`, `penalties`, `comparison`, `audit`).
   */
  llmPromptsBySection?: Record<string, string>;
  /** Global administrators only: wall-clock seconds for each section's completed LLM `ainvoke`. */
  llmInvokeWallSecondsBySection?: Record<string, number>;
  sections: {
    certificateOfNeedSummary: CertificateOfNeedSummary;
    sourceLinks: Citation[];
    licensingRequirements: LicensingRequirements;
    accreditationRequirements: AccreditationRequirements;
    complianceComparison: ComplianceComparison;
    /** CFR regulation sections linked to this facility type; injected from aggregator by rules engine. */
    cfrRegulationSections?: CfrRegulationSections;
    /** Facility-relevant Federal Register / source-document sections from aggregator. */
    sourceDocuments?: FacilitySourceDocuments;
    auditReadyChecklist: AuditChecklistItem[];
    penaltiesAndTotalExposure: PenaltiesAndTotalExposure;
    /** Accreditation program content (e.g. NIAHO DNV); present when rules engine injects from aggregator. */
    otherRequirements?: {
      niahoDnv?: AccreditationProgramData;
      [key: string]: AccreditationProgramData | undefined;
    };
  };
}

/** Response shape from POST /facility-compliance/parallel/penalties. */
export type FacilityCompliancePenaltiesResponse = {
  sections: { penaltiesAndTotalExposure: PenaltiesAndTotalExposure };
  entityDetails?: EntityDetails;
  llmPromptsBySection?: Record<string, string>;
  llmInvokeWallSecondsBySection?: Record<string, number>;
};

/** True when parallel compliance already includes penalty calendar or exposure rows. */
export function hasPenaltiesSectionLoaded(
  data:
    | FacilityComplianceData
    | FacilityCompliancePenaltiesResponse
    | null
    | undefined,
): boolean {
  const pe = data?.sections?.penaltiesAndTotalExposure;
  if (!pe || typeof pe !== "object") return false;
  const cal = pe.complianceCalendar;
  if (Array.isArray(cal) && cal.length > 0) return true;
  const items = pe.items;
  if (Array.isArray(items) && items.length > 0) return true;
  return false;
}

/** Merge `/parallel/penalties` response into an existing 5-section compliance payload. */
export function mergePenaltiesIntoFacilityComplianceData(
  existing: FacilityComplianceData,
  penaltiesResponse: FacilityCompliancePenaltiesResponse,
): FacilityComplianceData {
  if (
    !hasPenaltiesSectionLoaded(penaltiesResponse) &&
    hasPenaltiesSectionLoaded(existing)
  ) {
    return existing;
  }
  return {
    ...existing,
    entityDetails: penaltiesResponse.entityDetails ?? existing.entityDetails,
    llmPromptsBySection: {
      ...existing.llmPromptsBySection,
      ...penaltiesResponse.llmPromptsBySection,
    },
    llmInvokeWallSecondsBySection: {
      ...existing.llmInvokeWallSecondsBySection,
      ...penaltiesResponse.llmInvokeWallSecondsBySection,
    },
    sections: {
      ...existing.sections,
      penaltiesAndTotalExposure:
        penaltiesResponse.sections.penaltiesAndTotalExposure,
    },
  };
}

/**
 * Analysis stream completes after penalties may already be merged into React state.
 * Without this, `setComplianceData(analysisOnly)` wipes the penalties section.
 */
export function preservePenaltiesOnAnalysisUpdate(
  analysisData: FacilityComplianceData,
  existing: FacilityComplianceData | null | undefined,
): FacilityComplianceData {
  if (!existing || !hasPenaltiesSectionLoaded(existing)) {
    return analysisData;
  }
  if (hasPenaltiesSectionLoaded(analysisData)) {
    return analysisData;
  }
  return mergePenaltiesIntoFacilityComplianceData(analysisData, {
    sections: {
      penaltiesAndTotalExposure: existing.sections.penaltiesAndTotalExposure,
    },
    entityDetails: existing.entityDetails,
    llmPromptsBySection: existing.llmPromptsBySection,
    llmInvokeWallSecondsBySection: existing.llmInvokeWallSecondsBySection,
  });
}

export type ComplianceStreamEventHandler = (
  eventType: string,
  data: unknown,
) => void;

const EMPTY_COMPLIANCE_CITATION: Citation = {
  title: "",
  url: "",
  publisher: "",
  publicationDate: null,
  accessedDate: "",
};

const EMPTY_COMPLIANCE_FINDING: Finding = {
  text: "",
  citation: EMPTY_COMPLIANCE_CITATION,
};

const EMPTY_COMPLIANCE_AMOUNT: Amount = {
  amount: 0,
  currency: "USD",
  isEstimate: false,
};

export function emptyCertificateOfNeedSummary(): CertificateOfNeedSummary {
  return {
    projectDescription: "",
    conditionsOfApproval: [],
    approvedCapitalCost: EMPTY_COMPLIANCE_AMOUNT,
    timeline: {
      applicationDate: "",
      reviewDate: "",
      issuanceDate: "",
      expirationDate: "",
    },
    findings: {
      need: EMPTY_COMPLIANCE_FINDING,
      financialFeasibility: EMPTY_COMPLIANCE_FINDING,
      structureProcessOfCare: EMPTY_COMPLIANCE_FINDING,
      costContainment: EMPTY_COMPLIANCE_FINDING,
    },
    financialFeasibilityAnalysis: {
      capitalCost: EMPTY_COMPLIANCE_AMOUNT,
      payerMix: { Medicare: 0, Medicaid: 0, Commercial: 0, SelfPay: 0 },
      charityCarePercent: 0,
      notes: EMPTY_COMPLIANCE_FINDING,
    },
    criteriaSatisfied: [],
    costContainmentDiscussion: EMPTY_COMPLIANCE_FINDING,
    publicComments: [],
    screeningResponses: EMPTY_COMPLIANCE_FINDING,
    statusCheck: {
      currentLicenseUnits: 0,
      cnCompletionVerified: null,
      followUps: [],
    },
    bedNeedMethodology: {
      methodSummary: EMPTY_COMPLIANCE_FINDING,
      occupancyTargets: [],
      projections: [],
    },
    financialProjections: [],
  };
}

export function emptyPenaltiesAndTotalExposure(): PenaltiesAndTotalExposure {
  return {
    items: [],
    complianceCalendar: [],
    rollup: {
      recurringAnnualCosts: [],
      contingentRisks: [],
      assumptions: {
        licensedUnits: 0,
        fteCount: null,
        priorYearRevenue: EMPTY_COMPLIANCE_AMOUNT,
      },
    },
  };
}

export function createEmptyFacilityComplianceSections(): FacilityComplianceData["sections"] {
  return {
    certificateOfNeedSummary: emptyCertificateOfNeedSummary(),
    sourceLinks: [],
    licensingRequirements: { country: [], state: [], county: [], city: [] },
    accreditationRequirements: {
      conditionsOfParticipation: [],
      accreditingOrganizations: [],
      programSpecific: [],
    },
    complianceComparison: {
      completed: [],
      pending: [],
      updates: [],
      deadlines: [],
    },
    auditReadyChecklist: [],
    penaltiesAndTotalExposure: emptyPenaltiesAndTotalExposure(),
  };
}

export function createEmptyFacilityComplianceData(): FacilityComplianceData {
  return {
    sections: createEmptyFacilityComplianceSections(),
  };
}

/** Merge one SSE `section` event into an in-progress compliance payload. */
export function mergeComplianceStreamSection(
  base: FacilityComplianceData,
  sectionKey: string,
  sectionData: unknown,
): FacilityComplianceData {
  const sections = { ...base.sections } as Record<string, unknown>;
  sections[sectionKey] = sectionData;
  if (
    sectionKey === "licensingRequirements" &&
    sectionData &&
    typeof sectionData === "object" &&
    "sourceLinks" in (sectionData as object)
  ) {
    const links = (sectionData as { sourceLinks?: Citation[] }).sourceLinks;
    if (Array.isArray(links)) {
      sections.sourceLinks = links;
    }
  }
  return {
    ...base,
    sections: sections as FacilityComplianceData["sections"],
  };
}

async function readComplianceSseStream(
  endpoint: string,
  payload: FacilityCompliancePayload,
  onEvent: ComplianceStreamEventHandler,
  signal?: AbortSignal,
): Promise<void> {
  const baseUrl = API_BASE_URL;
  const fullUrl = `${baseUrl}${endpoint}`;
  const headers = {
    ..._buildAuthAndPreviewHeaders(),
    Accept: "text/event-stream",
  };
  const effectivePayload = _applyPreviewVerticalToPayload(
    payload as unknown as Record<string, unknown>,
  );

  const response = await fetch(fullUrl, {
    method: "POST",
    headers,
    body: JSON.stringify(effectivePayload),
    signal,
  });

  check401(response);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No reader available for compliance SSE stream");
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";
  let currentData = "";

  const dispatchEvent = (eventType: string, data: string) => {
    if (!eventType || !data) return;
    let parsedData: unknown = data;
    try {
      parsedData = JSON.parse(data);
    } catch {
      // Non-JSON SSE payloads remain valid event data.
    }
    if (eventType === "error") {
      const message =
        parsedData &&
        typeof parsedData === "object" &&
        "error" in parsedData &&
        typeof (parsedData as { error?: unknown }).error === "string"
          ? (parsedData as { error: string }).error
          : typeof parsedData === "string"
            ? parsedData
            : "Facility compliance analysis failed.";
      throw new Error(message);
    }
    onEvent(eventType, parsedData);
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      const trimmed = line.replace(/\r$/, "");
      if (trimmed.startsWith("event:")) {
        currentEvent = trimmed.slice(6).trim();
      } else if (trimmed.startsWith("data:")) {
        currentData = trimmed.slice(5).trim();
      } else if (trimmed === "" && currentEvent && currentData) {
        dispatchEvent(currentEvent, currentData);
        currentEvent = "";
        currentData = "";
      }
    }
  }

  if (currentEvent && currentData) {
    dispatchEvent(currentEvent, currentData);
  }
}

export async function streamFacilityComplianceParallel(
  payload: FacilityCompliancePayload,
  onEvent: ComplianceStreamEventHandler,
  signal?: AbortSignal,
): Promise<void> {
  await subscribeComplianceSseStream(
    "/api/v1/facility-compliance/stream",
    payload,
    onEvent,
    signal,
    "analysis",
  );
}

export async function streamFacilityCompliancePenaltiesOnly(
  payload: FacilityCompliancePayload,
  onEvent: ComplianceStreamEventHandler,
  signal?: AbortSignal,
): Promise<void> {
  await subscribeComplianceSseStream(
    "/api/v1/facility-compliance/parallel/penalties/stream",
    payload,
    onEvent,
    signal,
    "penalties",
  );
}

export interface FacilityCompliancePayload {
  user_id: number;
  organization: string;
  facility_name: string;
  facility_type: string;
  country: string;
  state: string;
  county: string;
  city: string;
  site_url: string;
  documents: string;
  as_of_date: string;
  response_language: string;
  instructions: string;
  output_format: string;
  org_name: string;
  last_medicare_revalidation_date?: string | null;
  last_medicaid_revalidation_date?: string | null;
  /** When set, backend can refresh medicare_certification_date for this facility before compliance run */
  facility_id?: number | null;
  /** FK to facility_type; preferred for state_facility_requirements URL lookup when set */
  facility_type_id?: number | null;
  /** Vertical slug for rules API cache key; preview flow may override via _applyPreviewVerticalToPayload */
  compliance_type?: string | null;
  vertical?: string | null;
  primary_profile_slug?: string | null;
  profile_targeting_json?: Record<string, unknown> | null;
  activity_domains?: string[];
  operation_contexts?: string[];
  asset_types?: string[];
  certificates_or_approvals?: string[];
  regulation_packs?: string[];
  overlay_packs?: string[];
  penalty_packs?: string[];
  /** When true (default), all 6 sections including penalties run in a single parallel batch. Set false only if penalties must be deferred separately. */
  include_penalties?: boolean;
  /** When false, rules API omits TITLE26 rows from complianceCalendar; when omitted, legacy full calendar. */
  show_tax_compliance_calendar?: boolean;
}

export interface FederalCompliancePayload {
  country: string;
  facilityType: string;
  state: string;
  cityOrCounty: string;
  constructionOrFitOut: boolean;
  dispensesOrStoresDrugs: boolean;
  emergencyServices: boolean;
  handlesControlledSubstances: boolean;
  instructions: string;
  org_name: string;
  output_format: string;
  ownership: string;
  payers: string[];
  question: string;
  response_language: string;
  servicesOffered: string[];
  usesOnsiteLab: boolean;
  usesRadiationOrImaging: boolean;
  /** Slim federal-only LLM path + aggregator CFR catalog (rules API); keeps parallel unchanged */
  federal_compliance_only?: boolean;
}

/** Dedupe concurrent identical POST /api/v1/facilities/ (StrictMode, effect re-runs). */
const inflightFederalCompliance = new Map<string, Promise<unknown>>();

function _normalizeFederalComplianceKeyPart(value: string | undefined | null): string {
  return (value ?? "").trim().toLowerCase();
}

/** Stable key aligned with rules API facilities Redis cache inputs. */
export function buildFederalComplianceInflightKey(
  payload: FederalCompliancePayload,
): string {
  const payers = [...(payload.payers ?? [])].map(_normalizeFederalComplianceKeyPart).sort();
  const services = [...(payload.servicesOffered ?? [])]
    .map(_normalizeFederalComplianceKeyPart)
    .sort();
  const keyData = {
    cityOrCounty: _normalizeFederalComplianceKeyPart(payload.cityOrCounty),
    constructionOrFitOut: payload.constructionOrFitOut === true,
    country: _normalizeFederalComplianceKeyPart(payload.country),
    dispensesOrStoresDrugs: payload.dispensesOrStoresDrugs === true,
    emergencyServices: payload.emergencyServices === true,
    facilityType: _normalizeFederalComplianceKeyPart(payload.facilityType),
    federal_compliance_only: payload.federal_compliance_only === true,
    handlesControlledSubstances: payload.handlesControlledSubstances === true,
    instructions: _normalizeFederalComplianceKeyPart(payload.instructions),
    output_format: _normalizeFederalComplianceKeyPart(payload.output_format),
    ownership: _normalizeFederalComplianceKeyPart(payload.ownership),
    payers,
    question: _normalizeFederalComplianceKeyPart(payload.question),
    response_language: _normalizeFederalComplianceKeyPart(payload.response_language),
    servicesOffered: services,
    state: _normalizeFederalComplianceKeyPart(payload.state),
    usesOnsiteLab: payload.usesOnsiteLab === true,
    usesRadiationOrImaging: payload.usesRadiationOrImaging === true,
  };
  return JSON.stringify(keyData);
}

/** Test-only: clear in-flight federal compliance dedupe map. */
export function clearFederalComplianceInflightForTests(): void {
  inflightFederalCompliance.clear();
}

/** Dedupe concurrent identical POST /api/v1/facility-compliance/parallel* (StrictMode, effect re-runs). */
const inflightParallelCompliance = new Map<
  string,
  Promise<FacilityComplianceData>
>();
const inflightParallelPenalties = new Map<
  string,
  Promise<FacilityCompliancePenaltiesResponse>
>();
const inflightCybersecurityPenaltyExposure = new Map<
  string,
  Promise<CybersecurityPenaltyExposureResponse>
>();

function _normalizeParallelKeyPart(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim().toLowerCase();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "1" : "0";
  return String(value).trim().toLowerCase();
}

function _stableJsonForInflightKey(value: unknown): string {
  if (value == null) return "";
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

function _sortedStringListForInflightKey(
  values: string[] | undefined | null,
): string[] {
  return [...(values ?? [])].map(_normalizeParallelKeyPart).sort();
}

/**
 * Stable key for parallel compliance POST dedupe (aligned with rules cache inputs).
 * When `penaltiesOnly` is true, uses the `/parallel/penalties` endpoint key space.
 */
export function buildFacilityComplianceParallelInflightKey(
  payload: FacilityCompliancePayload,
  options?: { penaltiesOnly?: boolean; streamAnalysis?: boolean },
): string {
  let mode = "parallel";
  if (options?.penaltiesOnly) {
    mode = "penalties";
  } else if (options?.streamAnalysis) {
    mode = "stream_analysis";
  }
  const keyData = {
    mode,
    facility_id: payload.facility_id ?? "",
    facility_type_id: payload.facility_type_id ?? "",
    facility_name: _normalizeParallelKeyPart(payload.facility_name),
    facility_type: _normalizeParallelKeyPart(payload.facility_type),
    country: _normalizeParallelKeyPart(payload.country),
    state: _normalizeParallelKeyPart(payload.state),
    city: _normalizeParallelKeyPart(payload.city),
    county: _normalizeParallelKeyPart(payload.county),
    documents: _normalizeParallelKeyPart(payload.documents),
    as_of_date: _normalizeParallelKeyPart(payload.as_of_date),
    org_name: _normalizeParallelKeyPart(payload.org_name),
    include_penalties: payload.include_penalties !== false,
    show_tax_compliance_calendar:
      payload.show_tax_compliance_calendar === true,
    compliance_type: _normalizeParallelKeyPart(
      payload.compliance_type ?? payload.vertical,
    ),
    primary_profile_slug: _normalizeParallelKeyPart(payload.primary_profile_slug),
    profile_targeting_json: _stableJsonForInflightKey(
      payload.profile_targeting_json,
    ),
    activity_domains: _sortedStringListForInflightKey(payload.activity_domains),
    operation_contexts: _sortedStringListForInflightKey(
      payload.operation_contexts,
    ),
    asset_types: _sortedStringListForInflightKey(payload.asset_types),
    certificates_or_approvals: _sortedStringListForInflightKey(
      payload.certificates_or_approvals,
    ),
    regulation_packs: _sortedStringListForInflightKey(payload.regulation_packs),
    overlay_packs: _sortedStringListForInflightKey(payload.overlay_packs),
    penalty_packs: _sortedStringListForInflightKey(payload.penalty_packs),
    last_medicare_revalidation_date:
      payload.last_medicare_revalidation_date ?? "",
    last_medicaid_revalidation_date:
      payload.last_medicaid_revalidation_date ?? "",
  };
  return JSON.stringify(keyData);
}

/** Test-only: clear in-flight parallel compliance dedupe maps. */
export function clearParallelComplianceInflightForTests(): void {
  inflightParallelCompliance.clear();
  inflightParallelPenalties.clear();
  inflightCybersecurityPenaltyExposure.clear();
  clearComplianceSseInflightForTests();
}

type ComplianceSseInflightEntry = {
  promise: Promise<void>;
  subscribers: Set<ComplianceStreamEventHandler>;
  eventBuffer: Array<[string, unknown]>;
};

const inflightAnalysisSseStream = new Map<string, ComplianceSseInflightEntry>();
const inflightPenaltiesSseStream = new Map<string, ComplianceSseInflightEntry>();

function _dispatchComplianceSseEvent(
  entry: ComplianceSseInflightEntry,
  eventType: string,
  data: unknown,
): void {
  entry.eventBuffer.push([eventType, data]);
  entry.subscribers.forEach((subscriber) => {
    try {
      subscriber(eventType, data);
    } catch {
      /* subscriber errors must not break shared stream */
    }
  });
}

/** Test-only: clear in-flight SSE stream dedupe maps. */
export function clearComplianceSseInflightForTests(): void {
  inflightAnalysisSseStream.clear();
  inflightPenaltiesSseStream.clear();
}

export function isComplianceSseStreamInflight(
  payload: FacilityCompliancePayload,
  mode: "analysis" | "penalties",
): boolean {
  const inflightKey = buildFacilityComplianceParallelInflightKey(
    payload,
    mode === "penalties"
      ? { penaltiesOnly: true }
      : { streamAnalysis: true },
  );
  const map =
    mode === "penalties" ? inflightPenaltiesSseStream : inflightAnalysisSseStream;
  return map.has(inflightKey);
}

async function subscribeComplianceSseStream(
  endpoint: string,
  payload: FacilityCompliancePayload,
  onEvent: ComplianceStreamEventHandler,
  signal: AbortSignal | undefined,
  mode: "analysis" | "penalties",
): Promise<void> {
  const inflightKey = buildFacilityComplianceParallelInflightKey(
    payload,
    mode === "penalties"
      ? { penaltiesOnly: true }
      : { streamAnalysis: true },
  );
  const inflightMap =
    mode === "penalties" ? inflightPenaltiesSseStream : inflightAnalysisSseStream;

  const existing = inflightMap.get(inflightKey);
  if (existing) {
    for (const [eventType, data] of existing.eventBuffer) {
      onEvent(eventType, data);
    }
    existing.subscribers.add(onEvent);
    return existing.promise;
  }

  const entry: ComplianceSseInflightEntry = {
    promise: Promise.resolve(),
    subscribers: new Set([onEvent]),
    eventBuffer: [],
  };

  const runPromise = readComplianceSseStream(
    endpoint,
    payload,
    (eventType, data) => {
      _dispatchComplianceSseEvent(entry, eventType, data);
    },
    signal,
  ).finally(() => {
    if (inflightMap.get(inflightKey) === entry) {
      inflightMap.delete(inflightKey);
    }
  });

  entry.promise = runPromise;
  inflightMap.set(inflightKey, entry);
  return runPromise;
}

/** Facility/session fields used to infer federal POST /facilities/ booleans */
export type FederalComplianceFacilityHint = {
  facility_id?: number | null;
  facility_type?: string | null;
  primary_profile_slug?: string | null;
  facility_type_slug?: string | null;
  lab_anchor_active?: boolean | null;
  dea_registration_expiration?: string | null;
};

/**
 * Map session/facility hints to FacilitiesRequest booleans for POST /api/v1/facilities/.
 * Merges `filtered_selectedFacility` when it matches the active facility id.
 */
export function deriveFederalComplianceFacilityFlags(
  facility: FederalComplianceFacilityHint | null,
): Pick<
  FederalCompliancePayload,
  | "usesOnsiteLab"
  | "usesRadiationOrImaging"
  | "dispensesOrStoresDrugs"
  | "handlesControlledSubstances"
> {
  const session = getSelectedFacilityFromSession<Record<string, unknown>>();
  const sameSite =
    facility &&
    session &&
    Number(session.facility_id) === Number(facility.facility_id);
  const merged: Record<string, unknown> = sameSite
    ? { ...session, ...facility }
    : { ...(facility ?? {}), ...(session ?? {}) };

  const ft = String(merged.facility_type ?? "").toLowerCase();
  const slug = String(merged.facility_type_slug ?? "").toLowerCase();
  const prof = String(merged.primary_profile_slug ?? "").toLowerCase();
  const haystack = `${ft} ${slug} ${prof}`;

  const usesOnsiteLab =
    merged.lab_anchor_active === true ||
    /\b(laboratory|pathology|\blab\b|clia|clinical\s*lab)\b/i.test(haystack);

  const usesRadiationOrImaging =
    /\b(radiation|imaging|oncology|x-?ray|fluorosc|ct\s|mri\b|nuclear\s+medic)\b/i.test(
      haystack,
    );

  const dispensesOrStoresDrugs =
    /\b(pharmacy|dispens|drug\s+store|retail\s+pharm)\b/i.test(haystack);

  const deaRaw = merged.dea_registration_expiration;
  const handlesControlledSubstances =
    (deaRaw != null && String(deaRaw).trim() !== "") ||
    /\b(behavioral|opioid|methadone|807|controlled)\b/i.test(haystack);

  return {
    usesOnsiteLab,
    usesRadiationOrImaging,
    dispensesOrStoresDrugs,
    handlesControlledSubstances,
  };
}

// Comprehensive Requirements Types
export interface RegulatoryRequirement {
  id: number;
  requirement_code: string;
  title: string;
  description: string;
  regulation_reference: string;
  country_code: string;
  jurisdiction_level: string;
  jurisdiction_name: string | null;
  regulatory_body: string;
  category: string;
  priority_level: number;
  effective_date: string | null;
  last_updated: string;
  is_active: boolean;
  frequency: string;
  mandate_level: string;
  responsible_person: string;
  evidence_documentation: string;
  summary: string;
  reference_url: string;
  conditional_applicability: string | null;
  issuing_authority_url: string | null;
  regulatory_citation_url: string | null;
  verification_url: string | null;
}

export interface OperationalDocument {
  id: number;
  document_code: string;
  document_name: string;
  source_url: string;
  document_url: string;
  compliance_type: string;
  notes: string;
  as_of_date: string;
  jurisdiction_level: string;
}

// CFR Regulation Section from aggregator (detailed regulatory text)
export interface CFRRegulationSection {
  regulation_section_id: number;
  section_identifier: string | null;
  section_name: string | null;
  active: boolean;
  regulation_part_id: number;
  part_number: string;
  part_name: string | null;
  cfr_title: number | null;
  regulation_type: string | null; // 'CFR', 'USC'
  source_system: string | null; // 'eCFR', 'USCode'
  regulation_group_id: number;
  group_short_name: string; // e.g., 'FDA', 'DEA', 'NRC'
  group_name: string | null;
  ecfr_section_id: number | null;
  ecfr_identifier: string | null;
  linkage_created_at: string | null;
  /** Optional URL from API (aggregator or derived eCFR link). */
  section_url?: string | null;
}

export interface FacilitySourceDocumentSection {
  id?: number | string;
  document_number: string;
  citation?: string | null;
  source_system?: string | null;
  title?: string | null;
  anchor_id?: string | null;
  heading?: string | null;
  heading_path?: string[] | string | null;
  excerpt?: string | null;
  text?: string | null;
  source_url?: string | null;
  cfr_references?: string[];
}

export interface FacilitySourceDocuments {
  total_sections: number;
  documents: Array<{
    document_number: string;
    citation?: string | null;
    title: string;
    source_system?: string | null;
    source_url?: string | null;
    sections: FacilitySourceDocumentSection[];
  }>;
}

export interface ComprehensiveRequirementsResponse {
  facility_type_slug: string;
  facility_display_name: string;
  facility_category: string;
  country_code: string;
  state_province: string;
  regulatory_requirements: RegulatoryRequirement[];
  operational_documents: OperationalDocument[];
  cfr_regulation_sections?: CFRRegulationSection[]; // Optional for backward compatibility
  total_regulatory_requirements: number;
  total_operational_documents: number;
  total_cfr_sections?: number; // Optional for backward compatibility
}

export interface ComprehensiveRequirementsParams {
  facilityTypeSlug: string;
  countryCode: string;
  stateProvince: string;
}

export interface RegulationTextSearchResult {
  source_type: "cfr_text" | "federal_register";
  group_label: string;
  title: string;
  snippet: string;
  section_identifier?: string | null;
  cfr_title?: number | null;
  section_url?: string | null;
  regulation_group?: string | null;
  regulation_section_id?: number | null;
  rank?: number;
}

export interface RegulationTextSearchResponse {
  query: string;
  total_count: number;
  results: RegulationTextSearchResult[];
}

export interface RegulationTextSearchParams {
  facilityTypeSlug: string;
  countryCode: string;
  stateProvince?: string;
  query: string;
  limit?: number;
  signal?: AbortSignal;
}

// State Requirements Types (new API)
export interface FacilityStateRequirement {
  state: string;
  facility_type_index: number;
  facility_type_name: string;
  license_or_regulatory_program: string;
  legal_code_reference: string;
  certificate_of_need_required: string;
  construction_review_required: string;
  medicare_certification_survey_required: string;
  medicaid_enrollment_revalidation_required: string;
  license_renewal_cycle_notes: string;
  fire_life_safety_building_oversight: string;
  staffing_credentialing_requirements: string;
  quality_reporting_obligations: string;
  application_submission_title: string;
  application_submission_url: string;
  medicare_enrollment_url: string;
  medicaid_enrollment_url: string;
  certificate_of_need_application_url: string;
  construction_review_services_url: string;
}

export interface StateRequirementsResponse {
  state: string;
  country_code: string;
  total_count: number;
  facility_requirements: FacilityStateRequirement[];
}

/** Cybersecurity penalty exposure API types (align with rules server / aggregator). */
export interface CyberPenaltyStructure {
  per_violation_min?: number | null;
  per_violation_max?: number | null;
  per_day_min?: number | null;
  per_day_max?: number | null;
  annual_cap?: number | null;
}

export interface CyberExposureVector {
  regulatory_regime?: string;
  cfr_reference?: string;
  violation_category?: string;
  penalty_structure?: CyberPenaltyStructure;
}

export interface CyberExposure {
  exposure_vectors: CyberExposureVector[];
  linked_penalty_detail?: Record<string, unknown>;
}

export interface CybersecurityPenaltyExposureResponse {
  has_coverage: boolean;
  exposure?: CyberExposure;
}

export interface GetCybersecurityPenaltyExposureParams {
  facility_type: string;
  country_code?: string;
  vertical?: string;
  as_of_date?: string;
}

/** POST /api/v1/enforcement/search (rules server). */
export interface EnforcementSearchRequestPayload {
  facility_name: string;
  city: string;
  state: string;
  county: string;
  year: number;
  /** Required by rules API (FK to facility_type); omit only in legacy mocks. */
  facility_type_id: number;
  ccn?: string;
  npi?: string;
  force_refresh?: boolean;
}

export interface EnforcementActionRecord {
  title?: string;
  /** API may return numeric or string amounts from upstream sources */
  amount?: number | string;
  amount_formatted?: string;
  date?: string;
  issuing_authority?: string;
  description?: string;
  violation_code?: string;
  case_number?: string;
  source_url?: string;
  jurisdiction?: string;
  source?: string;
  regulator?: string;
  source_system?: string;
  action_type?: string;
  case_type?: string;
  affected_individuals?: number | string | null;
  document_url?: string | null;
  /** From rules API when present */
  severity?: "Low" | "Moderate" | "High";
  /** SOM-style deficiency tags (e.g. F689) from rules API; may be empty for non-deficiency citations */
  tags?: string[];
  /** Provenance from rules (e.g. regulatory_facility_event merge) */
  raw_data?: Record<string, unknown>;
}

export interface EnforcementSearchResponse {
  actions?: EnforcementActionRecord[];
  penalties?: EnforcementActionRecord[];
  items?: EnforcementActionRecord[];
  total_amount?: number;
  sources_searched?: string[];
  errors?: string[];
  cache_hit?: boolean;
  persisted_hit?: boolean;
  /** ISO timestamp from rules API when the search ran */
  search_timestamp?: string;
  search_request_trace?: string;
  /** Regulatory-body filter labels (first item is all-sources sentinel) */
  regulatory_authority_filters?: string[];
}

/** POST /api/v1/enforcement/compliance-history */
export interface EnforcementComplianceHistoryRequest {
  facility_name: string;
  city?: string | null;
  state: string;
  county?: string | null;
  facility_type_id: number;
  year_from: number;
  year_to: number;
  ccn?: string | null;
  npi?: string | null;
}

export interface EnforcementComplianceHistoryYearBlock {
  year: number;
  total_amount: number;
  total_penalties: number;
  actions: EnforcementActionRecord[];
}

export interface EnforcementComplianceHistoryResponse {
  country_code?: string;
  vertical?: string;
  regulatory_authority_filters?: string[];
  years?: EnforcementComplianceHistoryYearBlock[];
  regulatory_open_data_events?: Record<string, unknown>[];
}

export interface EnforcementAnalyzeDocumentRequest {
  document_text: string;
  document_url?: string | null;
  state_code?: string | null;
}

export interface EnforcementAnalyzeDocumentResponse {
  summary_html?: string;
}

export interface HistoricalPenaltySomTagContext {
  tag: string;
  title?: string | null;
  description?: string | null;
  cfr_citation?: string | null;
  summary?: string | null;
  requirements?: string[];
  procedures?: string[];
  structured?: Record<string, unknown> | null;
  sources?: Array<Record<string, unknown> | string>;
  query_result?: string | null;
  error?: string | null;
}

export interface HistoricalPenaltyAnalysisRequest {
  facility: {
    facility_name: string;
    city?: string | null;
    state?: string | null;
    county?: string | null;
    country?: string | null;
    facility_type?: string | null;
    facility_type_id?: number | null;
  };
  analysis_year: number;
  as_of_date?: string | null;
  penalties: EnforcementActionRecord[];
  som_tag_context: HistoricalPenaltySomTagContext[];
}

export interface HistoricalPenaltyAnalysis {
  analysis_date: string;
  facility_name: string;
  analysis_year: number;
  total_penalty_exposure: string;
  executive_summary: string;
  tag_clusters: Array<{
    tag: string;
    regulation_area: string;
    penalty_count: number;
    total_amount: string;
    risk_level: string;
    recurrence_rationale: string;
    recommended_monitoring_frequency: string;
  }>;
  penalty_to_prevention_cards: Array<{
    penalty_event: string;
    regulation: string;
    root_cause: string;
    required_controls: string[];
    evidence_needed: string[];
    calendar_alerts: string[];
    owner: string;
    status: string;
    executive_escalation: string;
  }>;
  calendar_alerts: Array<{
    title: string;
    priority: string;
    audience: string;
    recommended_frequency: string;
    due_window: string;
    message: string;
    related_tags: string[];
  }>;
  evidence_checks: Array<{
    check: string;
    evidence: string;
    owner: string;
    frequency: string;
    related_tags: string[];
  }>;
  owner_escalations: Array<{
    owner: string;
    trigger: string;
    escalation_path: string;
    timeframe: string;
  }>;
  recommendations: Array<{
    recommendation: string;
    priority: string;
    rationale: string;
    expected_outcome: string;
  }>;
  partial_analysis_warnings: string[];
}

export interface HistoricalPenaltyAnalysisResponse {
  success: boolean;
  message: string;
  analysis: HistoricalPenaltyAnalysis;
  metadata: Record<string, unknown>;
}

export interface OcrEnforcementActionsParams {
  regulation?: string;
  start_year?: number;
  end_year?: number;
  case_type?: string;
  source_system?: string;
}

export interface OcrEnforcementActionsResponse {
  actions: EnforcementActionRecord[];
  count?: number;
  sources_searched?: string[];
}

const API_BASE_URL = getApiOrigin();

export const facilityComplianceService = {
  async getFacilityCompliance(
    payload: FacilityCompliancePayload,
  ): Promise<FacilityComplianceData> {
    try {
      const baseUrl = API_BASE_URL;
      const endpoint = "/api/v1/facility-compliance/";
      const fullUrl = `${baseUrl}${endpoint}`;

      console.log("🔗 Making API call to:", fullUrl);
      console.log("📦 Payload:", payload);
      console.log("🏥 Facility Compliance API - State and City:", {
        state: payload.state || "(empty)",
        city: payload.city || "(empty)",
        facility_type: payload.facility_type,
        country: payload.country,
      });

      const token = getValidAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(fullUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      check401(response);

      console.log("📡 Response status:", response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ API Error Response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`,
        );
      }

      console.log("✅ Data loaded through API");
      const data = await response.json();
      console.log("📊 Response data structure:", {
        hasEntityDetails: !!data.entityDetails,
        hasSections: !!data.sections,
        generatedAt: data.generatedAt,
      });
      return data as FacilityComplianceData;
    } catch (error) {
      console.error("❌ Error fetching facility compliance data:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        console.error(
          "🌐 Network error - check if API server is running and accessible",
        );
      }
      throw error;
    }
  },

  async getAccreditations(params: {
    facilityType: string;
    countryCode?: string;
    vertical?: string;
    stateProvince?: string;
  }): Promise<AccreditationsResponse> {
    const baseUrl = API_BASE_URL;
    const searchParams = new URLSearchParams({
      facility_type: params.facilityType,
      country_code: params.countryCode ?? "USA",
      vertical: params.vertical ?? "healthcare",
    });
    if (params.stateProvince?.trim()) {
      searchParams.set("state_province", params.stateProvince.trim());
    }
    const url = `${baseUrl}/api/v1/accreditations/?${searchParams.toString()}`;
    const token = getValidAccessToken();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const response = await fetch(url, { method: "GET", headers });
    check401(response);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Accreditations API error: ${response.status} ${errorText}`);
    }
    return (await response.json()) as AccreditationsResponse;
  },

  /**
   * Fetch program content for multiple accrediting bodies in a single call.
   * Calls GET /api/v1/accreditations/?body_slugs=...&facility_type=... and returns
   * a dict keyed by body_slug.  Used by the Accreditations sub-tab.
   */
  async getMultiAccreditationPrograms(params: {
    bodySlugs: string[];
    facilityType: string;
    countryCode?: string;
    vertical?: string;
  }): Promise<MultiBodyAccreditationsResponse> {
    if (!params.bodySlugs.length) return {};
    const baseUrl = API_BASE_URL;
    const searchParams = new URLSearchParams({
      facility_type: params.facilityType,
      country_code: params.countryCode ?? "USA",
      vertical: params.vertical ?? "healthcare",
    });
    for (const slug of params.bodySlugs) {
      searchParams.append("body_slugs", slug);
    }
    const url = `${baseUrl}/api/v1/accreditations/?${searchParams.toString()}`;
    const token = getValidAccessToken();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const response = await fetch(url, { method: "GET", headers });
    check401(response);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Multi-body accreditations API error: ${response.status} ${errorText}`);
    }
    return (await response.json()) as MultiBodyAccreditationsResponse;
  },

  /**
   * Fetch the list of accrediting bodies applicable to a facility type.
   * Returns bodies with their bodySlug (e.g. "tjc") — the correct identifier
   * for the multi-body programs endpoint.
   * Calls GET /api/v1/accreditations/bodies
   */
  async getAccreditingBodies(params: {
    facilityType: string;
    countryCode?: string;
    vertical?: string;
  }): Promise<AccreditingBodiesResponse> {
    const baseUrl = API_BASE_URL;
    const endpoint = "/api/v1/accreditations/bodies";
    const queryParams = new URLSearchParams({
      facility_type: params.facilityType,
      country_code: params.countryCode || "USA",
      vertical: params.vertical || "healthcare",
    });
    const fullUrl = `${baseUrl}${endpoint}?${queryParams.toString()}`;
    const token = getValidAccessToken();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const response = await fetch(fullUrl, { method: "GET", headers });
    check401(response);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Accrediting bodies API error: ${response.status} ${errorText}`);
    }
    return (await response.json()) as AccreditingBodiesResponse;
  },

  async getFederalCompliance(
    payload: FederalCompliancePayload,
    options?: { signal?: AbortSignal },
  ): Promise<any> {
    const headers = _buildAuthAndPreviewHeaders();
    const previewed = _applyPreviewVerticalToPayload(
      payload as unknown as Record<string, unknown>,
    );
    const effectivePayload = (previewed ?? payload) as unknown as FederalCompliancePayload;

    const inflightKey = buildFederalComplianceInflightKey(effectivePayload);
    const existing = inflightFederalCompliance.get(inflightKey);
    if (existing) {
      return existing;
    }

    const requestPromise = (async () => {
      try {
        const baseUrl = API_BASE_URL;
        const endpoint = "/api/v1/facilities/";
        const fullUrl = `${baseUrl}${endpoint}`;

        const response = await fetch(fullUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(effectivePayload),
          signal: options?.signal,
        });

        check401(response);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        const aborted =
          (error instanceof DOMException && error.name === "AbortError") ||
          (error instanceof Error && error.name === "AbortError");
        if (!aborted) {
          console.error("Error fetching federal compliance data:", error);
        }
        throw error;
      } finally {
        inflightFederalCompliance.delete(inflightKey);
      }
    })();

    inflightFederalCompliance.set(inflightKey, requestPromise);
    return requestPromise;
  },

  async getComprehensiveRequirements(
    params: ComprehensiveRequirementsParams,
  ): Promise<ComprehensiveRequirementsResponse> {
    try {
      const baseUrl = API_BASE_URL;
      const endpoint = `/api/v1/industry-compliance/facility-types/${params.facilityTypeSlug}/comprehensive-requirements`;
      const queryParams = new URLSearchParams({
        country_code: params.countryCode,
        state_province: params.stateProvince,
      });
      const fullUrl = `${baseUrl}${endpoint}?${queryParams.toString()}`;

      console.log("🔗 Fetching comprehensive requirements from:", fullUrl);
      console.log("📦 Params:", params);

      const token = getValidAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(fullUrl, {
        method: "GET",
        headers,
      });

      check401(response);

      console.log("📡 Response status:", response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ API Error Response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`,
        );
      }

      const data = await response.json();
      console.log("✅ Comprehensive requirements loaded successfully");
      console.log("📊 Response data:", {
        facilityType: data.facility_display_name,
        totalRequirements: data.total_regulatory_requirements,
        totalDocuments: data.total_operational_documents,
      });

      return data as ComprehensiveRequirementsResponse;
    } catch (error) {
      console.error("❌ Error fetching comprehensive requirements:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        console.error(
          "🌐 Network error - check if API server is running and accessible",
        );
      }
      throw error;
    }
  },

  async searchRegulationText(
    params: RegulationTextSearchParams,
  ): Promise<RegulationTextSearchResponse> {
    const baseUrl = API_BASE_URL;
    const endpoint = `/api/v1/industry-compliance/facility-types/${params.facilityTypeSlug}/search`;
    const queryParams = new URLSearchParams({
      country_code: params.countryCode,
      q: params.query,
    });
    if (params.stateProvince) {
      queryParams.set("state_province", params.stateProvince);
    }
    if (params.limit != null) {
      queryParams.set("limit", String(params.limit));
    }
    const fullUrl = `${baseUrl}${endpoint}?${queryParams.toString()}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "GET",
      headers,
      signal: params.signal,
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as RegulationTextSearchResponse;
  },

  // Streaming endpoint for progressive loading
  getStreamingEndpoint(): string {
    const baseUrl = API_BASE_URL;
    return `${baseUrl}/api/v1/facility-compliance/stream`;
  },

  // Parallel endpoint for faster generation (non-streaming)
  async getFacilityComplianceParallel(
    payload: FacilityCompliancePayload,
  ): Promise<FacilityComplianceData> {
    const inflightKey = buildFacilityComplianceParallelInflightKey(payload);
    const existing = inflightParallelCompliance.get(inflightKey);
    if (existing) {
      console.log(
        "🔄 Reusing in-flight parallel facility compliance request",
      );
      return existing;
    }

    const requestPromise = (async (): Promise<FacilityComplianceData> => {
      try {
        const baseUrl = API_BASE_URL;
        const endpoint = "/api/v1/facility-compliance/parallel";
        const fullUrl = `${baseUrl}${endpoint}`;

        const headers = _buildAuthAndPreviewHeaders();
        const effectivePayload = _applyPreviewVerticalToPayload(
          payload as unknown as Record<string, unknown>,
        );

        console.log("🚀 Making PARALLEL API call to:", fullUrl);
        console.log("📦 Payload:", effectivePayload);

        const response = await fetch(fullUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(effectivePayload),
        });

        check401(response);

        console.log("📡 Response status:", response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("❌ API Error Response:", errorText);
          throw new Error(
            `HTTP error! status: ${response.status}, message: ${errorText}`,
          );
        }

        console.log("✅ Parallel data loaded through API");
        const data = await response.json();
        return data as FacilityComplianceData;
      } catch (error) {
        console.error(
          "❌ Error fetching parallel facility compliance data:",
          error,
        );
        throw error;
      }
    })().finally(() => {
      if (inflightParallelCompliance.get(inflightKey) === requestPromise) {
        inflightParallelCompliance.delete(inflightKey);
      }
    });

    inflightParallelCompliance.set(inflightKey, requestPromise);
    return requestPromise;
  },

  /** Fetch only penalties section (for Potential Penalties tab when main load used include_penalties: false). */
  async getFacilityCompliancePenaltiesOnly(
    payload: FacilityCompliancePayload,
  ): Promise<FacilityCompliancePenaltiesResponse> {
    const inflightKey = buildFacilityComplianceParallelInflightKey(payload, {
      penaltiesOnly: true,
    });
    const existing = inflightParallelPenalties.get(inflightKey);
    if (existing) {
      console.log(
        "🔄 Reusing in-flight parallel penalties-only compliance request",
      );
      return existing;
    }

    const requestPromise = (async (): Promise<FacilityCompliancePenaltiesResponse> => {
      try {
        const baseUrl = API_BASE_URL;
        const endpoint = "/api/v1/facility-compliance/parallel/penalties";
        const fullUrl = `${baseUrl}${endpoint}`;

        const token = getValidAccessToken();
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await fetch(fullUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        check401(response);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! status: ${response.status}, message: ${errorText}`,
          );
        }

        const data = await response.json();
        return data as FacilityCompliancePenaltiesResponse;
      } catch (error) {
        console.error(
          "❌ Error fetching penalties-only facility compliance:",
          error,
        );
        throw error;
      }
    })().finally(() => {
      if (inflightParallelPenalties.get(inflightKey) === requestPromise) {
        inflightParallelPenalties.delete(inflightKey);
      }
    });

    inflightParallelPenalties.set(inflightKey, requestPromise);
    return requestPromise;
  },

  // New API: Facility state requirements
  async getStateRequirements(
    params: ComprehensiveRequirementsParams,
  ): Promise<StateRequirementsResponse> {
    try {
      const baseUrl = API_BASE_URL;
      const endpoint = `/api/v1/industry-compliance/facility-types/${params.facilityTypeSlug}/state-requirements`;
      const queryParams = new URLSearchParams({
        country_code: (params.countryCode || "").trim(),
        state_province: (params.stateProvince || "").trim(),
      });
      const fullUrl = `${baseUrl}${endpoint}?${queryParams.toString()}`;

      console.log("🔗 Fetching state requirements from:", fullUrl);
      console.log("📦 Params:", params);

      const token = getValidAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(fullUrl, {
        method: "GET",
        headers,
      });

      check401(response);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ API Error Response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`,
        );
      }

      const data = await response.json();
      return data as StateRequirementsResponse;
    } catch (error) {
      console.error("❌ Error fetching state requirements:", error);
      throw error;
    }
  },

  async getCybersecurityPenaltyExposure(
    params: GetCybersecurityPenaltyExposureParams,
  ): Promise<CybersecurityPenaltyExposureResponse> {
    const inflightKey = JSON.stringify({
      ft: (params.facility_type ?? "").trim().toLowerCase(),
      cc: (params.country_code ?? "USA").trim().toLowerCase(),
      v: (params.vertical ?? "healthcare").trim().toLowerCase(),
      ad: (params.as_of_date ?? "").trim(),
    });
    const existing = inflightCybersecurityPenaltyExposure.get(inflightKey);
    if (existing) {
      return existing;
    }

    const baseUrl = API_BASE_URL;
    const query = new URLSearchParams({
      facility_type: params.facility_type,
      country_code: params.country_code ?? "USA",
      vertical: params.vertical ?? "healthcare",
    });
    if (params.as_of_date) {
      query.set("as_of_date", params.as_of_date);
    }
    const endpoint = "/api/v1/cybersecurity-penalty-exposure";
    const fullUrl = `${baseUrl}${endpoint}?${query.toString()}`;

    const requestPromise = (async (): Promise<CybersecurityPenaltyExposureResponse> => {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const response = await fetch(fullUrl, {
        method: "GET",
        headers,
        signal: controller.signal,
        mode: "cors",
      });
      check401(response);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`,
        );
      }

      const data = (await response.json()) as CybersecurityPenaltyExposureResponse;
      return data;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        throw new Error("Request timed out. Please check your connection and try again.");
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
    })().finally(() => {
      if (inflightCybersecurityPenaltyExposure.get(inflightKey) === requestPromise) {
        inflightCybersecurityPenaltyExposure.delete(inflightKey);
      }
    });

    inflightCybersecurityPenaltyExposure.set(inflightKey, requestPromise);
    return requestPromise;
  },

  async postEnforcementSearch(
    payload: EnforcementSearchRequestPayload,
  ): Promise<EnforcementSearchResponse> {
    const baseUrl = API_BASE_URL;
    const endpoint = "/api/v1/enforcement/search";
    const fullUrl = `${baseUrl}${endpoint}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as EnforcementSearchResponse;
  },

  async postEnforcementComplianceHistory(
    body: EnforcementComplianceHistoryRequest,
  ): Promise<EnforcementComplianceHistoryResponse> {
    const baseUrl = API_BASE_URL;
    const endpoint = "/api/v1/enforcement/compliance-history";
    const fullUrl = `${baseUrl}${endpoint}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as EnforcementComplianceHistoryResponse;
  },

  async postEnforcementSearchAsync(
    payload: EnforcementSearchRequestPayload,
  ): Promise<{ job_id: number; status: string; poll_url: string }> {
    const baseUrl = API_BASE_URL;
    const endpoint = "/api/v1/enforcement/search/async";
    const fullUrl = `${baseUrl}${endpoint}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as { job_id: number; status: string; poll_url: string };
  },

  async getEnforcementSearchJob(jobId: number): Promise<{
    job_id: number;
    status: string;
    error_message: string | null;
    response: EnforcementSearchResponse | null;
  }> {
    const baseUrl = API_BASE_URL;
    const endpoint = `/api/v1/enforcement/jobs/${jobId}`;
    const fullUrl = `${baseUrl}${endpoint}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "GET",
      headers,
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as {
      job_id: number;
      status: string;
      error_message: string | null;
      response: EnforcementSearchResponse | null;
    };
  },

  async postEnforcementAnalyzeDocument(
    body: EnforcementAnalyzeDocumentRequest,
  ): Promise<EnforcementAnalyzeDocumentResponse> {
    const baseUrl = API_BASE_URL;
    const endpoint = "/api/v1/enforcement/analyze-document";
    const fullUrl = `${baseUrl}${endpoint}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as EnforcementAnalyzeDocumentResponse;
  },

  async postHistoricalPenaltyAnalysis(
    body: HistoricalPenaltyAnalysisRequest,
  ): Promise<HistoricalPenaltyAnalysisResponse> {
    const baseUrl = API_BASE_URL;
    const endpoint = "/api/v1/enforcement/historical-penalty-analysis";
    const fullUrl = `${baseUrl}${endpoint}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as HistoricalPenaltyAnalysisResponse;
  },

  async getOcrEnforcementActions(
    params: OcrEnforcementActionsParams = {},
  ): Promise<OcrEnforcementActionsResponse> {
    const baseUrl = API_BASE_URL;
    const query = new URLSearchParams({
      regulation: params.regulation ?? "HIPAA",
    });
    if (params.start_year != null) query.set("start_year", String(params.start_year));
    if (params.end_year != null) query.set("end_year", String(params.end_year));
    if (params.case_type?.trim()) query.set("case_type", params.case_type.trim());
    if (params.source_system?.trim()) {
      query.set("source_system", params.source_system.trim());
    }
    const fullUrl = `${baseUrl}/api/v1/enforcement/ocr?${query.toString()}`;

    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "GET",
      headers,
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return (await response.json()) as OcrEnforcementActionsResponse;
  },
};

export default facilityComplianceService;
